import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { Add, Delete, PointOfSale, Receipt as ReceiptIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { SalesService } from '../services/salesService';
import { InventoryService } from '../services/inventoryService';
import { ServiceService } from '../services/serviceService';
import { SalonService } from '../services/salonService';
import { useAuth } from '../context/AuthContext';
import { Sale, SaleItem, Product, Service, Salon } from '../types';
import Receipt from '../components/Receipt';

interface SaleFormData {
  customerId?: string;
  paymentMethod: 'cash' | 'card' | 'transfer';
  notes?: string;
}

export default function Sales() {
  const { appUser } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [saleItems, setSaleItems] = useState<SaleItem[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [recentSales, setRecentSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [salon, setSalon] = useState<Salon | null>(null);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<SaleFormData>({
    defaultValues: {
      paymentMethod: 'cash',
      customerId: '',
      notes: '',
    },
  });

  // Mock services data (you would fetch this from database)
  useEffect(() => {
    const mockServices: Service[] = [
      { id: '1', salonId: appUser?.salonId || '', name: 'Regular Haircut', price: 2000, category: 'barbing', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: '2', salonId: appUser?.salonId || '', name: 'Beard Trim', price: 1500, category: 'barbing', isActive: true, createdAt: new Date(), updatedAt: new Date() },
      { id: '3', salonId: appUser?.salonId || '', name: 'Full Service', price: 3500, category: 'barbing', isActive: true, createdAt: new Date(), updatedAt: new Date() },
    ];
    setServices(mockServices);
  }, [appUser]);

  // Load products and recent sales
  useEffect(() => {
  const loadData = async () => {
    if (!appUser?.salonId) return;

    try {
      const [productsData, servicesData, recentSalesData, salonData] = await Promise.all([
        InventoryService.getProducts(appUser.salonId),
        ServiceService.getServices(appUser.salonId),
        SalesService.getSales(appUser.salonId).then(sales => sales.slice(0, 10)),
        SalonService.getSalon(appUser.salonId),
      ]);

      setProducts(productsData);
      setServices(servicesData);
      setRecentSales(recentSalesData);
      setSalon(salonData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

    loadData();
  }, [appUser]);

  const handleShowReceipt = (sale: Sale) => {
    setSelectedSale(sale);
    setShowReceipt(true);
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedSale(null);
  };

  const addServiceToSale = (service: Service) => {
    const newItem: SaleItem = {
      id: Date.now().toString(),
      serviceId: service.id,
      type: 'service',
      name: service.name,
      quantity: 1,
      unitPrice: service.price,
      totalPrice: service.price,
    };

    setSaleItems([...saleItems, newItem]);
  };

  const addProductToSale = (product: Product) => {
    const newItem: SaleItem = {
      id: Date.now().toString(),
      productId: product.id,
      type: 'product',
      name: product.name,
      quantity: 1,
      unitPrice: product.price,
      totalPrice: product.price,
    };

    setSaleItems([...saleItems, newItem]);
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    setSaleItems(saleItems.map(item =>
      item.id === itemId
        ? { ...item, quantity, totalPrice: item.unitPrice * quantity }
        : item
    ));
  };

  const removeItem = (itemId: string) => {
    setSaleItems(saleItems.filter(item => item.id !== itemId));
  };

  const calculateTotal = () => {
    return saleItems.reduce((total, item) => total + item.totalPrice, 0);
  };

  const onSubmit = async (data: SaleFormData) => {
    if (!appUser?.salonId || saleItems.length === 0) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await SalesService.createSale(
        appUser.salonId,
        appUser.id,
        saleItems, // Pass full saleItems array with IDs
        data.paymentMethod,
        data.customerId,
        data.notes
      );

      setSuccess('Sale recorded successfully!');
      setSaleItems([]);
      reset();

      // Refresh recent sales
      const updatedSales = await SalesService.getSales(appUser.salonId);
      setRecentSales(updatedSales.slice(0, 10));

      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to record sale');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Sales Management
      </Typography>

      <Grid container spacing={3}>
        {/* Quick Add Services */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Barbing Services
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {services.filter(s => s.isActive).map((service) => (
                  <Chip
                    key={service.id}
                    label={`${service.name} - ₦${service.price}`}
                    onClick={() => addServiceToSale(service)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Add Products */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Products
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {products.filter(p => p.isActive && p.currentStock > 0).map((product) => (
                  <Chip
                    key={product.id}
                    label={`${product.name} - ₦${product.price}`}
                    onClick={() => addProductToSale(product)}
                    color="secondary"
                    variant="outlined"
                    disabled={product.currentStock === 0}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Current Sale */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Sale Items
              </Typography>

              {saleItems.length === 0 ? (
                <Typography color="text.secondary">
                  No items added. Click on services or products above to add them.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {saleItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.type}
                              size="small"
                              color={item.type === 'service' ? 'primary' : 'secondary'}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => updateItemQuantity(item.id, parseInt(e.target.value) || 1)}
                              inputProps={{ min: 1 }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell>₦{item.unitPrice.toLocaleString()}</TableCell>
                          <TableCell>₦{item.totalPrice.toLocaleString()}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => removeItem(item.id)}
                              color="error"
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={4} align="right">
                          <Typography variant="h6">Total:</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="h6">₦{calculateTotal().toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {saleItems.length > 0 && (
                <Box mt={2}>
                  <Button
                    variant="contained"
                    startIcon={<PointOfSale />}
                    onClick={() => setOpenDialog(true)}
                  >
                    Complete Sale
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Sales */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Sales
              </Typography>
              {recentSales.length === 0 ? (
                <Typography color="text.secondary">No sales recorded yet.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Items</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Payment</TableCell>
                        <TableCell>Receipt</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentSales.map((sale) => (
                        <TableRow key={sale.id}>
                          <TableCell>
                            {sale.createdAt.toLocaleDateString()} {sale.createdAt.toLocaleTimeString()}
                          </TableCell>
                          <TableCell>{sale.items.length} item(s)</TableCell>
                          <TableCell>₦{sale.totalAmount.toLocaleString()}</TableCell>
                          <TableCell>
                            <Chip label={sale.paymentMethod} size="small" />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleShowReceipt(sale)}
                              color="primary"
                              title="Print Receipt"
                            >
                              <ReceiptIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Complete Sale Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          console.log('Dialog closed');
          setOpenDialog(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Complete Sale</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Typography variant="h6" gutterBottom>
              Total: ₦{calculateTotal().toLocaleString()}
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="paymentMethod"
                  control={control}
                  rules={{ required: 'Payment method is required' }}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Payment Method</InputLabel>
                      <Select {...field} label="Payment Method">
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="card">Card</MenuItem>
                        <MenuItem value="transfer">Bank Transfer</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="customerId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Customer ID (Optional)"
                      placeholder="Enter customer ID if applicable"
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Notes (Optional)"
                      multiline
                      rows={2}
                      placeholder="Any additional notes"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Complete Sale'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Receipt Dialog */}
      {showReceipt && selectedSale && salon && (
        <Receipt
          sale={selectedSale}
          salon={salon}
          onClose={handleCloseReceipt}
        />
      )}
    </Box>
  );
}
